// Shared, genuinely service-agnostic content for the ServicePage composer.
// These are brand-level facts and crew practices that are identical across pages,
// so individual configs can fall back to them instead of repeating copy.
//
// Every string here avoids em dashes and contains no fabricated claims.

import type {
  BulletSection,
  IconCard,
  TitledCardSection,
} from "./types";

export const BASE_URL = "https://lancasterparoofing.com";
export const PHONE = "717-725-7170";

/** Real, verifiable aggregate rating used in Review schema. */
export const AGGREGATE_RATING = { ratingValue: "5.0", reviewCount: "23" };

/** 23. Why choose Graystone (brand-level, identical across services). */
export const defaultWhyChooseUs: IconCard[] = [
  {
    iconKey: "home",
    title: "Locally Owned & Operated Since 2007",
    desc: "Our team is personally involved in every project. You work directly with us from the first estimate to the final walkthrough.",
  },
  {
    iconKey: "award",
    title: "A+ BBB Accredited",
    desc: "Graystone holds an A+ rating with the Better Business Bureau, backed by more than 18 years of honest work across Lancaster and York counties.",
  },
  {
    iconKey: "thumbsUp",
    title: "5.0 Star Reputation",
    desc: "Our customers have rated us 5.0 stars on Google and Yelp. We earn that score one roof and one honest estimate at a time.",
  },
  {
    iconKey: "shield",
    title: "GAF Certified Installation",
    desc: "As a GAF certified contractor we install manufacturer approved systems that qualify your roof for stronger, longer warranties.",
  },
  {
    iconKey: "clipboard",
    title: "No Shortcuts, No Surprises",
    desc: "Ice barrier where required, proper flashing, correct fastening, and a written scope of work, with no hidden fees.",
  },
  {
    iconKey: "check",
    title: "Locally Owned and Licensed",
    desc: "We are based in East Petersburg (PA license PA034872), insured, and we treat every home in our community the way we would treat our own.",
  },
];

/** 20. Safety practices (identical crew standards on every job). */
export const defaultSafetyPractices: BulletSection = {
  heading: "Safety and Site Protection",
  intro:
    "A clean, safe job site protects your family, your property, and our crew. These practices are standard on every Graystone project, large or small.",
  columns: 2,
  bullets: [
    {
      label: "Trained, safety focused crews",
      detail:
        "Our crews follow recognized fall protection and ladder safety standards every day they are on your roof.",
    },
    {
      label: "Fall protection and harnesses",
      detail:
        "Roof anchors and personal fall arrest equipment keep our team secure on steep and tall rooflines.",
    },
    {
      label: "Property protection",
      detail:
        "We tarp landscaping, cover shrubs and AC units, and protect siding and windows before tear off begins.",
    },
    {
      label: "Debris containment",
      detail:
        "Dump trailers and ground tarps catch falling material so debris never ends up in your yard or gardens.",
    },
    {
      label: "Magnetic nail cleanup",
      detail:
        "We run magnetic rollers across the lawn, driveway, and beds to collect stray nails and metal fasteners.",
    },
    {
      label: "Daily cleanup",
      detail:
        "We leave your property tidy at the end of every workday, not just at the end of the project.",
    },
  ],
};

/** 21. Technology we use (identical toolset across services). */
export const defaultTechnology: TitledCardSection = {
  heading: "Technology and Tools We Use",
  intro:
    "We pair old fashioned craftsmanship with modern tools that make inspections more accurate and projects run smoothly.",
  columns: 3,
  cards: [
    {
      iconKey: "ruler",
      title: "Digital Roof Measurements",
      desc: "Precise digital and satellite measurements let us scope materials accurately and quote with confidence.",
    },
    {
      iconKey: "eye",
      title: "Detailed Photo Documentation",
      desc: "We document the roof, attic, flashing, and any damage with photos so you can see exactly what we see.",
    },
    {
      iconKey: "droplets",
      title: "Moisture and Leak Tracing",
      desc: "We trace leaks to their true source, including attic and decking inspection, rather than guessing at patches.",
    },
    {
      iconKey: "file",
      title: "Written Estimates and Records",
      desc: "Itemized written proposals and clear records keep your project organized from estimate to final walkthrough.",
    },
    {
      iconKey: "clipboard",
      title: "Manufacturer Spec Compliance",
      desc: "We install to GAF and manufacturer specifications so your materials qualify for their strongest warranties.",
    },
    {
      iconKey: "phone",
      title: "Responsive Communication",
      desc: "You can reach our team directly. We answer questions quickly and keep you informed at every stage.",
    },
  ],
};

/** 18. Roofing manufacturers we install (shared across roofing service pages). */
export const roofingBrands: TitledCardSection = {
  heading: "Roofing Brands We Install",
  intro:
    "We install proven, manufacturer backed roofing systems. As a GAF certified contractor we can offer enhanced GAF warranty coverage on qualifying installations.",
  columns: 3,
  cards: [
    {
      iconKey: "shield",
      title: "GAF",
      desc: "North America's largest shingle manufacturer. As a GAF certified contractor we install Timberline HDZ and other GAF systems that qualify for enhanced warranty coverage.",
    },
    {
      iconKey: "shield",
      title: "Owens Corning",
      desc: "Known for Duration shingles with SureNail technology for strong wind resistance and a wide, fade resistant color range.",
    },
    {
      iconKey: "shield",
      title: "CertainTeed",
      desc: "A trusted name in architectural and designer shingles, with premium profiles for homeowners who want a distinctive look.",
    },
  ],
};

/** 18. Siding manufacturers we install (shared across siding pages). */
export const sidingBrands: TitledCardSection = {
  heading: "Siding Brands We Install",
  intro:
    "We work with leading siding manufacturers so we can match the right product to your home, your budget, and your maintenance preferences.",
  columns: 3,
  cards: [
    {
      iconKey: "home",
      title: "CertainTeed",
      desc: "A wide range of durable vinyl siding profiles and colors with strong fade resistance and proven Pennsylvania performance.",
    },
    {
      iconKey: "home",
      title: "James Hardie",
      desc: "Premium fiber cement siding engineered for our climate, with the look of real wood and exceptional durability.",
    },
    {
      iconKey: "home",
      title: "Royal and Mastic",
      desc: "Quality vinyl and insulated siding options that deliver lasting curb appeal and low maintenance at a fair value.",
    },
  ],
};
